// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { z } from 'zod';

/** Photo styling settings for CV templates. */
export const photoSettingsSchema = z.object({
  shape: z.enum(['circle', 'rounded', 'square']).default('circle'),
  zoom: z.number().min(1).max(2).default(1),
  offsetX: z.number().min(-50).max(50).default(0),
  offsetY: z.number().min(-50).max(50).default(0),
  filter: z.enum(['none', 'grayscale', 'sepia', 'high-contrast']).default('none'),
  size: z.enum(['small', 'medium', 'large']).default('medium'),
});

export type PhotoSettings = z.infer<typeof photoSettingsSchema>;

export const defaultPhotoSettings: PhotoSettings = {
  shape: 'circle',
  zoom: 1,
  offsetX: 0,
  offsetY: 0,
  filter: 'none',
  size: 'medium',
};

/** Profile form schema. All fields are optional (partial update). */
export const profileSchema = z.object({
  name: z
    .string()
    .max(255, 'Name must be at most 255 characters.')
    .optional()
    .or(z.literal('')),
  phone: z
    .string()
    .max(50, 'Phone must be at most 50 characters.')
    .optional()
    .or(z.literal('')),
  address: z
    .string()
    .max(500, 'Address must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
  professional_summary: z
    .string()
    .max(5000, 'Summary must be at most 5000 characters.')
    .optional()
    .or(z.literal('')),
  linkedin_url: z
    .string()
    .max(500, 'LinkedIn URL must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
  github_url: z
    .string()
    .max(500, 'GitHub URL must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
  portfolio_url: z
    .string()
    .max(500, 'Portfolio URL must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
  output_language_mode: z
    .enum(['detected', 'english', 'custom'])
    .default('detected'),
  custom_output_language: z
    .string()
    .max(100, 'Custom language must be at most 100 characters.')
    .optional()
    .or(z.literal('')),
  photo_url: z
    .string()
    .max(2000, 'Photo URL must be at most 2000 characters.')
    .optional()
    .or(z.literal('')),
  photo_settings: photoSettingsSchema.optional(),
});

export type ProfileFormData = z.infer<typeof profileSchema>;
